import {
  BrainCircuit,
  FileText,
  GitFork,
  ListTodo,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  ShieldCheck,
  UserRound,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client.js";
import { CommandSearchDialog } from "./command-search/CommandSearchDialog.js";
import { clearProtectedDestination, type ProtectedPath } from "./protectedDestination.js";
import { useAppSession } from "./AppSession.js";
import styles from "./AppShell.module.css";

const NAV_ITEMS = [
  { to: "/focus", label: "nav.focus", icon: BrainCircuit, showInDock: true },
  { to: "/work", label: "nav.work", icon: ListTodo, showInDock: true },
  { to: "/graph", label: "nav.graph", icon: GitFork, showInDock: true },
  { to: "/documents", label: "nav.documents", icon: FileText, showInDock: true },
] as const;

const READINESS_KEYS = {
  unpaired: "readiness.unpaired",
  no_grant: "readiness.noGrant",
  offline: "readiness.offline",
  model_unavailable: "readiness.modelUnavailable",
  ready: "readiness.ready",
} as const;
const NAVIGATION_COLLAPSED_STORAGE_KEY = "mind.shell.navigationCollapsed";
function readNavigationCollapsedPreference(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(NAVIGATION_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

export type BootScreenProps = {
  readonly error?: unknown;
  readonly onRetry?: () => void;
};

export function BootScreen({ error, onRetry }: BootScreenProps) {
  const { t } = useTranslation();
  return (
    <main className={styles.boot}>
      <div className={styles.bootMark} aria-hidden="true">
        <ShieldCheck size={28} />
      </div>
      <p className={styles.bootWordmark}>{t("app.name")}</p>
      {error === undefined ? (
        <p role="status">{t("app.loadingSecurely")}</p>
      ) : (
        <div className={styles.bootError}>
          <p role="alert">{t("errors.generic")}</p>
          {onRetry !== undefined && (
            <button type="button" onClick={onRetry}>{t("common.retry")}</button>
          )}
        </div>
      )}
    </main>
  );
}

type WorkspaceControlProps = {
  readonly compact?: boolean;
  readonly labelId: string;
};

function WorkspaceControl({ compact = false, labelId }: WorkspaceControlProps) {
  const { t } = useTranslation();
  const session = useAppSession();
  const location = useLocation();
  const navigate = useNavigate();

  const selectWorkspace = async (workspaceId: string) => {
    const destination = location.pathname as ProtectedPath;
    const search = { workspace: workspaceId };
    switch (destination) {
      case "/focus":
        await navigate({ to: "/focus", search, replace: true });
        return;
      case "/work":
        await navigate({ to: "/work", search, replace: true });
        return;
      case "/graph":
        await navigate({ to: "/graph", search, replace: true });
        return;
      case "/documents":
        await navigate({ to: "/documents", search, replace: true });
    }
  };

  if (session.workspaces.length === 0) {
    return compact ? null : <p className={styles.workspaceEmpty}>{t("shell.noWorkspace")}</p>;
  }


  return (
    <div className={compact ? styles.workspaceCompact : styles.workspaceControl}>
      <label id={labelId} htmlFor={`${labelId}-select`}>{t("shell.workspace")}</label>
      <select
        id={`${labelId}-select`}
        value={session.selectedWorkspace?.id ?? ""}
        onChange={(event) => void selectWorkspace(event.target.value)}
      >
        {session.workspaces.map((workspace) => (
          <option key={workspace.id} value={workspace.id}>{workspace.name}</option>
        ))}
      </select>
    </div>
  );
}

type SettingsSheetProps = {
  readonly dialogRef: RefObject<HTMLDialogElement | null>;
  readonly restoreFocusRef: RefObject<HTMLButtonElement | null>;
};

function SettingsSheet({ dialogRef, restoreFocusRef }: SettingsSheetProps) {
  const { t, i18n } = useTranslation();
  const session = useAppSession();
  const navigate = useNavigate();
  const [actionError, setActionError] = useState<unknown>(null);
  const identity = session.identity;
  if (identity === null) return null;

  const simulation = identity.simulation;
  const effective = identity.effective;
  const readiness = session.readiness;
  const preferenceError = session.preferenceError;
  const visibleError = preferenceError ?? actionError;
  const readinessText = readiness === null
    ? t("readiness.unavailable")
    : t(READINESS_KEYS[readiness.state]);
  const observedAt = readiness === null
    ? null
    : new Intl.DateTimeFormat(i18n.resolvedLanguage, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(readiness.observedAt));

  const showError = (error: unknown) => {
    if (error instanceof ApiError) {
      return t(error.messageKey, { ...error.params, defaultValue: t("errors.generic") });
    }
    return t("errors.generic");
  };

  return (
    <dialog
      ref={dialogRef}
      className={styles.settingsSheet}
      aria-labelledby="account-settings-title"
      onClose={() => restoreFocusRef.current?.focus()}
    >
      <div className={styles.sheetHeader}>
        <div>
          <p className={styles.eyebrow}>{t("shell.settings")}</p>
          <h2 id="account-settings-title">{t("shell.accountAndWorkspace")}</h2>
        </div>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t("common.close")}
          onClick={() => dialogRef.current?.close()}
        >
          <X aria-hidden="true" size={20} />
        </button>
      </div>

      {simulation !== null && (
        <section className={styles.simulationPanel} aria-labelledby="simulation-settings-title">
          <h3 id="simulation-settings-title">{t("shell.simulationMode")}</h3>
          <p>{t("identity.simulation", {
            mock: effective.displayName,
            actor: identity.actor.displayName,
          })}</p>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => {
              setActionError(null);
              void session.endSimulation()
                .then(async () => {
                  clearProtectedDestination();
                  dialogRef.current?.close();
                  await navigate({ to: "/focus", search: {}, replace: true });
                })
                .catch((error: unknown) => setActionError(error));
            }}
          >
            {t("identity.stopSimulation")}
          </button>
        </section>
      )}

      <section className={styles.settingsSection} aria-labelledby="identity-settings-title">
        <h3 id="identity-settings-title">{t("shell.account")}</h3>
        <dl className={styles.definitionList}>
          <div>
            <dt>{t("shell.effectiveAccount")}</dt>
            <dd>{effective.displayName}</dd>
          </div>
          {simulation !== null && (
            <div>
              <dt>{t("shell.actorAccount")}</dt>
              <dd>{identity.actor.displayName}</dd>
            </div>
          )}
        </dl>
      </section>

      <section className={styles.settingsSection} aria-labelledby="workspace-settings-title">
        <h3 id="workspace-settings-title">{t("shell.workspace")}</h3>
        <WorkspaceControl labelId="settings-workspace" />
      </section>

      <section className={styles.settingsSection} aria-labelledby="readiness-settings-title">
        <h3 id="readiness-settings-title">{t("shell.readiness")}</h3>
        <div className={styles.readinessDetail}>
          {readiness?.state === "ready"
            ? <Wifi aria-hidden="true" size={20} />
            : <WifiOff aria-hidden="true" size={20} />}
          <div>
            <p>{readinessText}</p>
            {readiness !== null && (
              <p className={styles.secondaryText}>
                {t("shell.modelObserved", { model: readiness.model, observedAt })}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className={styles.settingsSection} aria-labelledby="preferences-settings-title">
        <h3 id="preferences-settings-title">{t("shell.preferences")}</h3>
        <label className={styles.field}>
          <span>{t("shell.interfaceLanguage")}</span>
          <select
            value={effective.uiLocale}
            disabled={simulation !== null || session.preferencePending}
            onChange={(event) => {
              const locale = event.target.value === "en" ? "en" : "ru";
              void session.updatePreferences(locale, effective.reducedMotion).catch(() => undefined);
            }}
          >
            <option value="ru">{t("shell.locale.ru")}</option>
            <option value="en">{t("shell.locale.en")}</option>
          </select>
        </label>
        <label className={styles.toggleField}>
          <span>
            <strong>{t("shell.reducedMotion")}</strong>
            <small>{t("shell.reducedMotionHint")}</small>
          </span>
          <input
            type="checkbox"
            checked={effective.reducedMotion}
            disabled={simulation !== null || session.preferencePending}
            onChange={(event) => {
              void session.updatePreferences(effective.uiLocale, event.target.checked).catch(() => undefined);
            }}
          />
        </label>
        {simulation !== null && <p className={styles.secondaryText}>{t("shell.simulationPreferences")}</p>}
        {visibleError !== null && <p role="alert" className={styles.inlineError}>{showError(visibleError)}</p>}
      </section>

      <footer className={styles.sheetFooter}>
        <a href="/api/source">{t("legal.notice")}</a>
        {simulation === null && (
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => {
              setActionError(null);
              void session.signOut()
                .then(async () => {
                  clearProtectedDestination();
                  dialogRef.current?.close();
                  await navigate({ to: "/sign-in", replace: true });
                })
                .catch((error: unknown) => setActionError(error));
            }}
          >
            {t("shell.signOut")}
          </button>
        )}
      </footer>
    </dialog>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const session = useAppSession();
  const location = useLocation();
  const navigate = useNavigate();
  const settingsDialog = useRef<HTMLDialogElement>(null);
  const settingsTrigger = useRef<HTMLButtonElement>(null);
  const commandSearchDialog = useRef<HTMLDialogElement>(null);
  const commandSearchInvoker = useRef<HTMLButtonElement>(null);
  const desktopCommandSearchButton = useRef<HTMLButtonElement>(null);
  const mobileCommandSearchButton = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const railNavigationToggleRef = useRef<HTMLButtonElement>(null);
  const restoreNavigationToggleRef = useRef<HTMLButtonElement>(null);
  const previousNavigationCollapsed = useRef(false);
  const previousCommandSearchOpen = useRef(false);
  const [navigationCollapsed, setNavigationCollapsed] = useState(readNavigationCollapsedPreference);
  const [commandSearchOpen, setCommandSearchOpen] = useState(false);
  const [simulationExitPending, setSimulationExitPending] = useState(false);
  const [simulationExitError, setSimulationExitError] = useState<unknown>(null);
  const identity = session.identity;

  useEffect(() => {
    const heading = mainRef.current?.querySelector<HTMLElement>("h1");
    if (heading === undefined || heading === null) return;
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }, [location.pathname]);
  useEffect(() => {
    if (previousNavigationCollapsed.current === navigationCollapsed) return;
    previousNavigationCollapsed.current = navigationCollapsed;
    (navigationCollapsed ? restoreNavigationToggleRef : railNavigationToggleRef).current?.focus();
  }, [navigationCollapsed]);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        NAVIGATION_COLLAPSED_STORAGE_KEY,
        navigationCollapsed ? "true" : "false",
      );
    } catch {
      // Local preference storage is optional.
    }
  }, [navigationCollapsed]);
  useEffect(() => {
    if (commandSearchOpen) {
      previousCommandSearchOpen.current = true;
      return;
    }
    if (!previousCommandSearchOpen.current) return;
    previousCommandSearchOpen.current = false;
    queueMicrotask(() => {
      commandSearchInvoker.current?.focus();
    });
  }, [commandSearchOpen]);
  const openCommandSearch = useCallback((trigger?: HTMLButtonElement) => {
    const activeElement = typeof document !== "undefined" && document.activeElement instanceof HTMLButtonElement
      ? document.activeElement
      : null;
    const fallback = typeof window !== "undefined" && window.matchMedia("(max-width: 900px)").matches
      ? mobileCommandSearchButton.current
      : desktopCommandSearchButton.current;
    commandSearchInvoker.current = trigger ?? activeElement ?? fallback;
    setCommandSearchOpen(true);
  }, []);

  const closeCommandSearch = useCallback(() => {
    setCommandSearchOpen(false);
  }, []);

  if (identity === null) return <BootScreen />;

  const openSettings = (trigger: HTMLButtonElement) => {
    settingsTrigger.current = trigger;
    settingsDialog.current?.showModal();
  };
  const simulated = identity.simulation !== null;
  const readinessText = session.readiness === null
    ? t("readiness.unavailable")
    : t(READINESS_KEYS[session.readiness.state]);
  const exitSimulation = () => {
    setSimulationExitError(null);
    setSimulationExitPending(true);
    void session.endSimulation()
      .then(async () => {
        clearProtectedDestination();
        await navigate({ to: "/focus", search: {}, replace: true });
      })
      .catch((error: unknown) => setSimulationExitError(error))
      .finally(() => setSimulationExitPending(false));
  };

  return (
    <div className={styles.shell} data-navigation-collapsed={navigationCollapsed ? "true" : "false"}>
      {navigationCollapsed ? (
        <button
          ref={restoreNavigationToggleRef}
          type="button"
          className={styles.desktopNavigationToggle}
          aria-controls="desktop-primary-navigation"
          aria-expanded={false}
          aria-label={t("shell.showNavigation")}
          title={t("shell.showNavigation")}
          onClick={() => setNavigationCollapsed(false)}
        >
          <PanelLeftOpen aria-hidden="true" size={20} />
          <span>{t("shell.showNavigation")}</span>
        </button>
      ) : null}
      <aside className={styles.rail}>
        <div className={styles.railHeader}>
          <Link
            to="/focus"
            search={session.selectedWorkspace === null ? {} : { workspace: session.selectedWorkspace.id }}
            className={styles.brand}
            aria-label={t("app.name")}
          >
            <ShieldCheck aria-hidden="true" size={28} />
            <span>{t("app.name")}</span>
          </Link>
          <button
            ref={railNavigationToggleRef}
            type="button"
            className={styles.navigationToggle}
            aria-controls="desktop-primary-navigation"
            aria-expanded={true}
            aria-label={t("shell.hideNavigation")}
            title={t("shell.hideNavigation")}
            onClick={() => setNavigationCollapsed(true)}
          >
            <PanelLeftClose aria-hidden="true" size={20} />
          </button>
        </div>

        <WorkspaceControl compact labelId="rail-workspace" />
        <p className={styles.readinessSummary} role="status">
          <span>{t("shell.readiness")}</span>
          <strong>{readinessText}</strong>
        </p>
        <button
          ref={desktopCommandSearchButton}
          type="button"
          className={styles.searchButton}
          onClick={(event) => openCommandSearch(event.currentTarget)}
        >
          <Search aria-hidden="true" size={18} />
          <span>{t("commandSearch.trigger")}</span>
          <kbd>Ctrl K</kbd>
        </button>

        <nav id="desktop-primary-navigation" className={styles.navigation} aria-label={t("shell.primaryNavigation")}>
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              search={session.selectedWorkspace === null ? {} : { workspace: session.selectedWorkspace.id }}
              className={styles.navLink}
              activeProps={{ "data-active": true }}
            >
              <Icon aria-hidden="true" size={20} />
              <span>{t(label)}</span>
            </Link>
          ))}
        </nav>

        <div className={styles.railBottom}>
          <button
            ref={settingsTrigger}
            type="button"
            className={styles.accountButton}
            onClick={(event) => openSettings(event.currentTarget)}
          >
            <UserRound aria-hidden="true" size={20} />
            <span>
              <strong>{identity.effective.displayName}</strong>
              <small>{t("shell.settings")}</small>
            </span>
            <Settings aria-hidden="true" size={18} />
          </button>
        </div>
      </aside>

      <header className={styles.mobileHeader}>
        <Link
          to="/focus"
          search={session.selectedWorkspace === null ? {} : { workspace: session.selectedWorkspace.id }}
          className={styles.mobileBrand}
        >
          <ShieldCheck aria-hidden="true" size={24} />
          <span>{t("app.name")}</span>
        </Link>
        <div className={styles.mobileWorkspace} aria-label={t("shell.workspace")}>
          <span>{t("shell.workspace")}</span>
          <strong>{session.selectedWorkspace?.name ?? t("shell.noWorkspace")}</strong>
        </div>
        <button
          ref={mobileCommandSearchButton}
          type="button"
          className={styles.mobileSearchButton}
          onClick={(event) => openCommandSearch(event.currentTarget)}
        >
          <Search aria-hidden="true" size={18} />
          <span>{t("commandSearch.trigger")}</span>
        </button>
        <button
          type="button"
          className={styles.iconButton}
          aria-label={t("shell.openSettings")}
          onClick={(event) => openSettings(event.currentTarget)}
        >
          <UserRound aria-hidden="true" size={22} />
        </button>
      </header>

      <main ref={mainRef} className={styles.main}>
        {simulated ? (
          <aside className={styles.simulationNotice} aria-label={t("shell.simulationMode")} aria-busy={simulationExitPending}>
            <div className={styles.simulationNoticeCopy}>
              <strong>{t("identity.simulationNotice")}</strong>
              <span>{t("identity.simulation", {
                mock: identity.effective.displayName,
                actor: identity.actor.displayName,
              })}</span>
            </div>
            <button type="button" className={styles.simulationExitButton} onClick={exitSimulation} disabled={simulationExitPending}>
              {simulationExitPending ? t("common.working") : t("identity.exitSimulation")}
            </button>
          </aside>
        ) : null}
        {simulationExitError !== null ? <p className={styles.inlineError} role="alert">{t("errors.generic")}</p> : null}
        <div className={styles.page}>
          <Outlet />
        </div>
      </main>

      <nav className={styles.mobileDock} aria-label={t("shell.primaryNavigation")}>
        {NAV_ITEMS.filter((item) => item.showInDock).map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            search={session.selectedWorkspace === null ? {} : { workspace: session.selectedWorkspace.id }}
            className={styles.dockLink}
            activeProps={{ "data-active": true }}
          >
            <Icon aria-hidden="true" size={20} />
            <span>{t(label)}</span>
          </Link>
        ))}
      </nav>

      <SettingsSheet dialogRef={settingsDialog} restoreFocusRef={settingsTrigger} />
      <CommandSearchDialog
        open={commandSearchOpen}
        workspaceId={session.selectedWorkspace?.id ?? ""}
        dialogRef={commandSearchDialog}
        onRequestOpen={() => openCommandSearch()}
        onRequestClose={closeCommandSearch}
      />
    </div>
  );
}
